// Settings → Connected apps (/me/settings/apps): the URL to paste into an MCP
// client, which workspaces the connected apps reach, and the apps connected
// over OAuth with a way to disconnect each.
import { useQuery } from "@tanstack/react-query";
import { m } from "@trameria/cloud-i18n";
import {
  apiErrorWithLead,
  Button,
  confirmAction,
  ErrorLine,
  formatRelative,
  iconOf,
  SettingsBox,
  type SettingsItem,
  SettingsRow,
  SettingsSection,
  Skeleton,
  Switch,
  Tooltip,
  toast,
  useApi,
} from "@trameria/ui-core";
import { useState } from "react";
import {
  type ConnectedApp,
  type McpWorkspaceGrant,
  mcpQuery,
  useDisconnectApp,
  useSetMcpWorkspace,
} from "../queries";

export const appsSettings: SettingsItem = {
  id: "apps",
  order: 50,
  label: () => m.web_settings_apps(),
  icon: iconOf("plug"),
  description: () => m.web_settings_apps_description(),
  sections: [
    { id: "url", label: () => m.web_settings_apps_url() },
    { id: "workspaces", label: () => m.web_settings_apps_workspaces() },
    { id: "list", label: () => m.web_settings_apps_list() },
  ],
  Component: AppsSettings,
};

const SCOPES: Record<string, () => string> = {
  "trameria:read": () => m.web_settings_apps_scope_read(),
  "trameria:write": () => m.web_settings_apps_scope_write(),
  offline_access: () => m.web_settings_apps_scope_offline(),
  "trameria:desktop": () => m.web_settings_apps_scope_desktop(),
};

function AppsSettings() {
  const api = useApi();
  const mcp = useQuery(mcpQuery(api));
  const disconnect = useDisconnectApp();
  const [failedName, setFailedName] = useState<string | null>(null);
  const nameOf = (a: ConnectedApp) => a.name ?? a.clientId;

  async function remove(a: ConnectedApp) {
    const ok = await confirmAction({
      title: m.web_settings_apps_disconnect_title({ name: nameOf(a) }),
      body: m.web_settings_apps_disconnect_body(),
      action: m.web_settings_apps_disconnect(),
      danger: true,
    });
    if (!ok) return;
    setFailedName(null);
    disconnect.mutate(a.clientId, {
      onError: () => setFailedName(nameOf(a)),
    });
  }

  if (mcp.isError)
    return (
      <div className="mt-[14px] flex items-center gap-3 text-[13px] text-muted">
        {m.web_load_failed()}
        <Button onClick={() => void mcp.refetch()}>{m.web_retry()}</Button>
      </div>
    );
  if (!mcp.data)
    return (
      <div className="mt-[14px]">
        <Skeleton lines={[70, 50, 60]} />
      </div>
    );
  const { serverUrl, apps, workspaces } = mcp.data;
  return (
    <>
      {/* The URL is long: it gets the box's whole width under its name. */}
      <SettingsBox>
        <div id="url" className="scroll-mt-4 px-4 py-3">
          <div className="text-[13px] leading-[1.5] text-fg">
            {m.web_settings_apps_url()}
          </div>
          <div className="mt-px text-[11.5px] leading-[1.5] text-faint">
            {m.web_settings_apps_url_note()}
          </div>
          <div className="mt-2.5 flex items-center gap-3">
            <span className="min-w-0 flex-1 select-all break-all font-mono text-[12px] text-body">
              {serverUrl}
            </span>
            <Button
              className="flex-none"
              onClick={() =>
                void navigator.clipboard
                  .writeText(serverUrl)
                  .then(() => toast(m.web_settings_apps_copied()))
              }
            >
              {m.web_settings_apps_copy()}
            </Button>
          </div>
        </div>
      </SettingsBox>
      <McpWorkspaces workspaces={workspaces ?? []} />
      <SettingsSection id="list" label={m.web_settings_apps_list()}>
        {failedName && disconnect.isError && (
          <ErrorLine>
            {m.web_settings_apps_disconnect_failed({ name: failedName })}
          </ErrorLine>
        )}
        {apps.length === 0 ? (
          <p className="mt-2 text-[12.5px] leading-[1.7] text-faint">
            {m.web_settings_apps_empty()}
          </p>
        ) : (
          <SettingsBox>
            {apps.map((a) => (
              <SettingsRow
                key={a.clientId}
                label={nameOf(a)}
                description={m.web_settings_apps_app_description({
                  scopes: a.scopes.map((s) => SCOPES[s]?.() ?? s).join(" · "),
                  granted: formatRelative(a.grantedAt),
                })}
              >
                <Button variant="ghost" onClick={() => void remove(a)}>
                  {m.web_settings_apps_disconnect()}
                </Button>
              </SettingsRow>
            ))}
          </SettingsBox>
        )}
      </SettingsSection>
    </>
  );
}

/**
 * Which workspaces connected apps reach: one set for the account, for every
 * app (the OAuth scope says what an app may do, not where). One row per
 * workspace with a switch, saved as it is turned. Where the workspace's admins
 * do not allow MCP the switch shows off and cannot be turned (what is in
 * effect, even if the person allowed it before), the name is quieter, and the
 * switch tells why on hover or focus.
 */
function McpWorkspaces({
  workspaces,
}: {
  workspaces: readonly McpWorkspaceGrant[];
}) {
  const set = useSetMcpWorkspace();
  return (
    <SettingsSection id="workspaces" label={m.web_settings_apps_workspaces()}>
      <p className="mt-1 text-[12px] leading-[1.7] text-faint">
        {m.web_settings_apps_workspaces_note()}
      </p>
      {workspaces.length === 0 ? (
        <p className="mt-2 text-[12.5px] leading-[1.7] text-faint">
          {m.web_settings_apps_no_workspaces()}
        </p>
      ) : (
        <SettingsBox>
          {workspaces.map((w) => {
            const toggle = (
              <Switch
                label={
                  w.mcpAllowed
                    ? m.web_settings_apps_allow_workspace({ name: w.name })
                    : `${m.web_settings_apps_allow_workspace({ name: w.name })}: ${m.web_settings_apps_not_allowed()}`
                }
                checked={w.mcpAllowed && w.allowed}
                disabled={!w.mcpAllowed}
                onChange={(allowed) =>
                  set.mutate({ slug: w.slug, name: w.name, allowed })
                }
              />
            );
            return (
              <SettingsRow
                key={w.slug}
                label={
                  w.mcpAllowed ? (
                    w.name
                  ) : (
                    <span className="text-faint">{w.name}</span>
                  )
                }
              >
                {w.mcpAllowed ? (
                  toggle
                ) : (
                  <Tooltip text={m.web_settings_apps_not_allowed()} tap>
                    {/* A disabled button takes no pointer: the box around it
                        holds the reason. */}
                    <span className="inline-flex">{toggle}</span>
                  </Tooltip>
                )}
              </SettingsRow>
            );
          })}
        </SettingsBox>
      )}
      {set.isError && set.variables && (
        <ErrorLine>
          {apiErrorWithLead(
            set.error,
            m.web_settings_apps_workspace_failed({ name: set.variables.name }),
          )}
        </ErrorLine>
      )}
    </SettingsSection>
  );
}
